/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Chart from 'react-apexcharts';
import Loader from '../../../../component/common/Loader';
import { useCompanySectionFilter } from '../../../../component/B2B/CompanySectionLayout';
import { SidebarContext } from '../../../../context/SidebarContext';
import { getLocalStorageItem } from '../../../../utils/helper';
import {
  StandardButton,
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  BigStatPanel,
  ProgressList,
  Avatar,
  Chip,
  distributionBarOptions,
  PANEL_ROW_CLASS,
  STAT_ICONS,
  AREA_FILL,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID
} from '../../../../component/common/designSystem';
import {
  PHASE_COLORS,
  FEATURE_COLORS,
  TRACKER_COLORS,
  VOLUME_RAMP,
  DAILY_VOLUME_RAMP,
  ACHIEVEMENT_RAMP,
  ACTIVE_DAYS_RAMP,
  REPEAT_RAMP,
  TASK_PROGRESS_RAMP,
  challengePhaseChipClass
} from './challengesTheme';
import {
  fetchCompanyChallengesDashboard,
  formatBigNumber,
  lastNDaysLabels
} from './companyChallengesApi';

const dateLabel = (value) =>
  value ? new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '-';

/**
 * Company Challenges → Overview.
 *
 * Reads as five bands, the same shape as the Sleep, Breathwork and Habit
 * trackers:
 *  1. Headline stats — the programme, and how much of the workforce it reached.
 *  2. Distributions — how far people got and how hard they went.
 *  3. Trends over the last 30 days — joins, completions and tracked volume.
 *  4. Leaderboard and per-challenge performance.
 *  5. Composition — which trackers and app features the challenges ask for.
 *
 * "Volume" rather than "steps" throughout: the challenge decides which tracker
 * it reads, and the page takes its noun from `primaryDataTypeLabel`. Today
 * that always resolves to Steps.
 */
const CChallengesDashboard = () => {
  const navigate = useNavigate();
  const { isB2BFansMode } = useContext(SidebarContext);
  const { department } = useCompanySectionFilter();
  const userData = JSON.parse(getLocalStorageItem('userData') || '{}');
  const slug = userData?.slug;

  const [loader, setLoader] = useState(true);
  const [stats, setStats] = useState(null);

  const population = isB2BFansMode ? 'members' : 'employees';

  useEffect(() => {
    let mounted = true;
    setLoader(true);
    fetchCompanyChallengesDashboard(department).then((data) => {
      if (!mounted) return;
      setStats(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [department, isB2BFansMode]);

  const volumeLabel = stats?.primaryDataTypeLabel || 'Steps';
  // Prose noun for the tracker. Taken from the label, not the stored `unit` —
  // a config that records its unit as "count" would read "Total count".
  const volumeUnit = volumeLabel.toLowerCase();

  const joinsPerDay = stats?.joinsPerDay || [];
  const completionsPerDay = stats?.completionsPerDay || [];
  const volumePerDay = stats?.volumePerDay || [];
  const trendLabels = lastNDaysLabels(volumePerDay.length || 30);

  const volumeOptions = {
    chart: { ...CHART_BASE, type: 'area' },
    colors: [CHART_COLORS.brand],
    stroke: { curve: 'smooth', width: 2 },
    fill: AREA_FILL,
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: trendLabels, tickAmount: 6 },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} ${volumeUnit}` } }
  };

  const joinsOptions = {
    chart: { ...CHART_BASE, type: 'bar', stacked: false },
    colors: [CHART_COLORS.teal, CHART_COLORS.green],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: { categories: lastNDaysLabels(joinsPerDay.length || 30), tickAmount: 6 },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } }
  };

  const weekdayOptions = distributionBarOptions({
    labels: (stats?.weekdayPattern || []).map((day) => day.label),
    colors: [CHART_COLORS.brand],
    unit: volumeUnit
  });

  const hasVolume = volumePerDay.some((value) => value > 0);
  const hasJoins =
    joinsPerDay.some((value) => value > 0) || completionsPerDay.some((value) => value > 0);

  // A company with no challenges gets the reason, not a grid of zeroes —
  // every panel below would be empty and none of them would say why.
  if (!loader && stats && !stats.hasChallenges) {
    return (
      <div>
        <Helmet>
          <meta charSet="utf-8" />
          <title>Challenges Overview | Shoorah</title>
        </Helmet>
        <Panel title="No challenges yet">
          <PanelEmpty>
            You haven&apos;t created a challenge for your {population} yet. Once one is live, its
            participation and activity will show up here.
          </PanelEmpty>
          <div className="flex justify-center">
            <StandardButton
              text="Create a challenge"
              onClick={() => navigate(`/${slug}/challenges`)}
            />
          </div>
        </Panel>
      </div>
    );
  }

  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Challenges Overview | Shoorah</title>
      </Helmet>
      {loader ? <Loader /> : null}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <span className="text-sm font-medium text-shoorah-btn dark:text-white capitalize">
          {population}
        </span>
        <StandardButton
          text="Manage challenges"
          variant="secondary"
          onClick={() => navigate(`/${slug}/challenges`)}
        />
      </div>

      {/* ---------- Headline ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
        <StatCard
          icon={STAT_ICONS.catalog}
          title="Challenges Run"
          value={stats ? formatBigNumber(stats.totalChallenges) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.onlineNow}
          title="Running Now"
          value={stats ? formatBigNumber(stats.runningChallenges) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.users}
          title="Participants"
          value={stats ? formatBigNumber(stats.participants) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.score}
          title="Completion Rate"
          value={stats ? `${stats.completionRate}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.completions}
          title={`Total ${volumeLabel}`}
          value={stats ? formatBigNumber(stats.totalVolume) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.wearable}
          title="Wearables Connected"
          value={stats ? formatBigNumber(stats.wearableConnected) : '-'}
        />
      </div>

      {/* ---------- The two numbers the programme lives or dies on ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mt-6">
        <BigStatPanel
          title="Reach"
          subtitle={`Share of ${population} who joined a challenge`}
          value={stats ? `${stats.reachPercentage}%` : '-'}
          caption={
            stats
              ? `${stats.participants.toLocaleString()} of ${stats.totalUsers.toLocaleString()} ${population}`
              : null
          }
        />
        <BigStatPanel
          title="Daily Target Hit Rate"
          subtitle="Tracked days that met their target"
          value={stats ? `${stats.targetHitRate}%` : '-'}
          caption={
            stats
              ? `${stats.targetsHit.toLocaleString()} of ${stats.trackedRecords.toLocaleString()} tracked days`
              : null
          }
        />
        <Panel title="Participation" subtitle="Joins, completions and repeat take-up">
          <dl className="space-y-3 text-sm">
            {[
              ['Total joins', stats ? stats.totalJoins.toLocaleString() : '-'],
              ['Completed', stats ? stats.completedJoins.toLocaleString() : '-'],
              [
                'Challenges per participant',
                stats ? stats.avgChallengesPerParticipant.toFixed(1) : '-'
              ],
              [
                `Avg ${volumeUnit} per participant`,
                stats ? formatBigNumber(stats.avgVolumePerParticipant) : '-'
              ],
              [
                'Busiest day',
                stats && stats.bestDayVolume > 0
                  ? `${dateLabel(stats.bestDayDate)} — ${formatBigNumber(stats.bestDayVolume)}`
                  : '-'
              ]
            ].map(([label, value]) => (
              <div key={label} className="flex items-baseline justify-between gap-3">
                <dt className="text-gray-500 dark:text-gray-400">{label}</dt>
                <dd className="font-semibold text-shoorah-btn dark:text-white">{value}</dd>
              </div>
            ))}
          </dl>
        </Panel>
        <Panel title="On Offer" subtitle="Rewards attached to your challenges">
          <dl className="space-y-3 text-sm">
            {[
              ['Rewards', stats ? stats.rewardsOffered.toLocaleString() : '-'],
              ['Wellpoints', stats ? formatBigNumber(stats.wellPointsOnOffer) : '-'],
              ['Winner slots', stats ? stats.winnerSlots.toLocaleString() : '-'],
              ['Winners announced', stats ? stats.winnersAnnounced.toLocaleString() : '-'],
              [
                'Tracked tasks',
                stats ? `${stats.trackedTasks} of ${stats.totalTasks}` : '-'
              ]
            ].map(([label, value]) => (
              <div key={label} className="flex items-baseline justify-between gap-3">
                <dt className="text-gray-500 dark:text-gray-400">{label}</dt>
                <dd className="font-semibold text-shoorah-btn dark:text-white">{value}</dd>
              </div>
            ))}
          </dl>
        </Panel>
      </div>

      {/* ---------- Distributions ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <MiniDonutCard
          title="Challenge Phase"
          subtitle="Where each challenge sits today"
          data={stats?.phaseDistribution || []}
          colors={PHASE_COLORS}
          unit="challenges"
          legendLimit={4}
        />
        <MiniDonutCard
          title={`${volumeLabel} per Person`}
          subtitle="Lifetime, across all challenges"
          data={stats?.volumeDistribution || []}
          colors={VOLUME_RAMP}
          unit={population}
        />
        <MiniDonutCard
          title="A Typical Day"
          subtitle={`One person's ${volumeUnit} in a day`}
          data={stats?.dailyVolumeDistribution || []}
          colors={DAILY_VOLUME_RAMP}
          unit="days"
        />
        <MiniDonutCard
          title="Target Achievement"
          subtitle="How close tracked days came"
          data={stats?.achievementDistribution || []}
          colors={ACHIEVEMENT_RAMP}
          unit="days"
        />
        <MiniDonutCard
          title="Active Days"
          subtitle="Days with any tracked activity"
          data={stats?.activeDaysDistribution || []}
          colors={ACTIVE_DAYS_RAMP}
          unit={population}
        />
        <MiniDonutCard
          title="Repeat Participation"
          subtitle="Challenges joined per person"
          data={stats?.challengesPerUserDistribution || []}
          colors={REPEAT_RAMP}
          unit={population}
          legendLimit={4}
        />
        <MiniDonutCard
          title="Task Progress"
          subtitle="Share of a challenge's tasks finished"
          data={stats?.taskProgressDistribution || []}
          colors={TASK_PROGRESS_RAMP}
          unit="entries"
          legendLimit={4}
        />
        <MiniDonutCard
          title="Trackers Used"
          subtitle="Which wearable metric challenges read"
          data={stats?.trackerSplit || []}
          colors={TRACKER_COLORS}
          unit="records"
        />
      </div>

      {/* ---------- Trends ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel
          title={`${volumeLabel} Per Day`}
          subtitle={`Everything your ${population} tracked — last 30 days`}
        >
          {hasVolume ? (
            <Chart
              type="area"
              height={340}
              width="100%"
              series={[{ name: volumeLabel, data: volumePerDay }]}
              options={volumeOptions}
            />
          ) : (
            <PanelEmpty>No tracked activity yet.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Joins and Completions" subtitle="Challenge take-up — last 30 days">
          {hasJoins ? (
            <Chart
              type="bar"
              height={340}
              width="100%"
              series={[
                { name: 'Joined', data: joinsPerDay },
                { name: 'Completed', data: completionsPerDay }
              ]}
              options={joinsOptions}
            />
          ) : (
            <PanelEmpty>Nobody has joined a challenge yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Leaderboard and per-challenge performance ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel
          title="Leaderboard"
          subtitle={`Top 10 by ${volumeUnit}`}
          action={
            <StandardButton
              text={isB2BFansMode ? 'All members' : 'All employees'}
              variant="secondary"
              onClick={() => navigate(`/${slug}/challenges/insights/users`)}
            />
          }
        >
          {stats?.leaderboard?.length ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-3 font-medium">#</th>
                  <th className="py-2 pr-3 font-medium">Name</th>
                  <th className="py-2 pr-3 font-medium text-right">{volumeLabel}</th>
                  <th className="py-2 pr-3 font-medium text-right">Active days</th>
                  <th className="py-2 font-medium text-right">Targets hit</th>
                </>
              }
            >
              {stats.leaderboard.map((row) => (
                <tr
                  key={row.userId}
                  className={`${PANEL_ROW_CLASS} cursor-pointer hover:bg-shoorah-normanGray/40`}
                  onClick={() =>
                    navigate(`/${slug}/challenges/insights/user?userId=${row.userId}`)
                  }
                >
                  <td className="py-2.5 pr-3 font-semibold">{row.rank}</td>
                  <td className="py-2.5 pr-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <Avatar
                        src={row.profileUrl}
                        name={row.name}
                        className="w-8 h-8 text-xs"
                      />
                      <span className="truncate" title={row.email || row.name}>
                        {row.name}
                      </span>
                    </div>
                  </td>
                  <td className="py-2.5 pr-3 text-right">{row.volume.toLocaleString()}</td>
                  <td className="py-2.5 pr-3 text-right">{row.activeDays}</td>
                  <td className="py-2.5 text-right">{row.targetsHit}</td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>No tracked activity yet.</PanelEmpty>
          )}
        </Panel>

        <Panel
          title="Challenge Performance"
          subtitle="Most-joined challenges"
          action={
            <StandardButton
              text="All challenges"
              variant="secondary"
              onClick={() => navigate(`/${slug}/challenges/insights/challenges`)}
            />
          }
        >
          {stats?.topChallenges?.length ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-3 font-medium">Challenge</th>
                  <th className="py-2 pr-3 font-medium text-right">Joined</th>
                  <th className="py-2 pr-3 font-medium text-right">Completed</th>
                  <th className="py-2 font-medium text-right">{volumeLabel}</th>
                </>
              }
            >
              {stats.topChallenges.map((row) => (
                <tr key={row._id} className={PANEL_ROW_CLASS}>
                  <td className="py-2.5 pr-3">
                    <div className="min-w-0">
                      <p className="truncate" title={row.title}>
                        {row.title}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <Chip className={challengePhaseChipClass(row.phase)}>
                          {row.phaseLabel}
                        </Chip>
                        <span className="text-xs text-gray-400">
                          {dateLabel(row.startDate)} – {dateLabel(row.endDate)}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="py-2.5 pr-3 text-right">{row.participants}</td>
                  <td className="py-2.5 pr-3 text-right">
                    {row.completed}
                    <span className="ml-1 text-xs text-gray-400">({row.completionRate}%)</span>
                  </td>
                  <td className="py-2.5 text-right">{formatBigNumber(row.volume)}</td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>No challenges yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Composition ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mt-6">
        <Panel
          title="Weekday Pattern"
          subtitle={`When the ${volumeUnit} actually happen`}
        >
          {(stats?.weekdayPattern || []).some((day) => day.count > 0) ? (
            <Chart
              type="bar"
              height={320}
              width="100%"
              series={[
                {
                  name: volumeLabel,
                  data: (stats?.weekdayPattern || []).map((day) => day.count)
                }
              ]}
              options={weekdayOptions}
            />
          ) : (
            <PanelEmpty>No tracked activity yet.</PanelEmpty>
          )}
        </Panel>

        <Panel title="What Challenges Ask For" subtitle="App features behind the tasks you set">
          <ProgressList
            items={stats?.featureSplit || []}
            unit="tasks"
            color={FEATURE_COLORS[0]}
          />
        </Panel>

        {/* Phase says where a challenge sits on the calendar; status says what
            happened to it in review — Draft and Rejected are the ones a
            company admin needs to act on. */}
        <Panel title="Challenge Status" subtitle="Approval and lifecycle state">
          <ProgressList
            items={stats?.statusDistribution || []}
            unit="challenges"
            color={FEATURE_COLORS[2]}
          />
        </Panel>
      </div>
    </div>
  );
};

export default CChallengesDashboard;
